import { useState, useEffect } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import CSPFooter from "@/components/CSPFooter";
import { useVersion, formatVersion } from "@/context/VersionContext";

export default function Login() {
  const { login, user } = useAuth();
  const versionInfo = useVersion();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("expired") === "1") toast.info("Sessione scaduta: accedi di nuovo.");
  }, []);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Accesso effettuato");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-white items-center justify-center p-12 border-r border-zinc-200">
        <div className="relative z-10 flex flex-col items-center text-center max-w-md">
          <img
            src="/logo-csp.png"
            alt="Club Scherma Palermo"
            data-testid="login-logo"
            className="w-80 h-auto object-contain mb-8"
          />
          <p className="text-[13px] uppercase tracking-[0.2em]" style={{ color: "#b8860b" }}>Club Scherma Palermo</p>
          <p className="mt-6 text-zinc-600 text-sm">Gestione tecnica e prestazione atletica: monitora atleti, allenamenti, test e gare del club in un'unica piattaforma.</p>
          <div className="mt-12 w-full">
            <CSPFooter variant="app" />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6 flex items-center gap-3">
            <img src="/logo-mark.png" alt="CSP" className="w-14 h-14 object-contain" />
            <div>
              <div className="font-heading font-bold tracking-tight" style={{ color: "#1a237e" }}>CSP Lab</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "#b8860b" }}>Club Scherma Palermo</div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-3xl font-bold tracking-tight">Accedi</h2>
            <p className="text-zinc-500 mt-1">Inserisci le tue credenziali per continuare.</p>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" required />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-12 bg-black hover:bg-zinc-800 rounded-md">
              {loading ? "Accesso in corso…" : "Accedi"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-zinc-600">
            Non hai un account?{" "}
            <Link to="/registrati" data-testid="link-register" className="font-semibold text-black hover:underline">Registrati</Link>
          </div>

          <div className="lg:hidden mt-8">
            <CSPFooter variant="app" />
          </div>

          <div className="mt-10 pt-6 border-t border-zinc-100 text-center" data-testid="login-signature">
            <div className="text-[11px] tracking-wider text-zinc-400">CSP Lab · <span className="font-mono" data-testid="login-version">{formatVersion(versionInfo)}</span>{versionInfo.build_date && <span className="text-zinc-300 ml-1">· Build {versionInfo.build_date}</span>}</div>
            <div className="text-[10px] text-zinc-400 mt-0.5">
              Ideato e sviluppato da <span className="text-zinc-500">Massimo La Rosa</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
